import { useQuery } from '@tanstack/react-query'
import { Tooltip } from 'antd'
import { Fragment, type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMedia } from 'react-use'

import { API } from '@/api'
import ConfigureIcon from '@/assets/image-view/configure.svg'
import MachineActionIcon from '@/assets/image-view/machine-action.svg'
import { Button, EmptyView, Loading } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { isImageLaunchUnavailable } from '@/consts/image'
import { GhostType, MachineType, PaymentType } from '@/consts/machine'
import { BILLING_METHODS, BillingMethod, Duration } from '@/consts/order'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import { cn } from '@/utils/style'

import { useSelectionScope } from '../hooks/useSelectionScope'
import { getImageBrand } from '../utils/imageBrand'
import { IMAGE_BRAND_ICONS } from '../utils/imageBrandIcons'
import { ImageAttributeSelection, serializeImageAttributeFilters } from '../utils/imageFilters'
import { BOOT_SOURCE_PAGE_SIZE, BootSourcePagination } from './boot-source-pagination'
import { ImageAttributeFilters } from './ImageAttributeFilters'
import { MachineSelector } from './machine-selector'

interface Props {
  type: MachineType
  onTypeChange: (type: MachineType) => void
  machine?: Machine
  onMachineChange: (machine?: Machine) => void
  image?: Image
  onImageChange: (image?: Image) => void
  count: number
  keywords: string[]
  billingMethod: BillingMethod
  duration: Duration
  toolbarStart?: ReactNode
  toolbarEnd?: ReactNode
  supportsExtraResources?: boolean
}

export function ImageMachineSelector({
  type,
  onTypeChange,
  machine,
  onMachineChange,
  image,
  onImageChange,
  count,
  keywords,
  billingMethod,
  duration,
  toolbarStart,
  toolbarEnd,
  supportsExtraResources = billingMethod !== BILLING_METHODS.SUBSCRIPTION,
}: Props) {
  const { t } = useTranslation()
  const scope = useSelectionScope()
  const [imageType, setImageType] = useState(GhostType.Image)
  const [selection, setSelection] = useState<ImageAttributeSelection>({})
  const [pagination, setPagination] = useState({ contextKey: '', page: 1 })
  const [collapsed, setCollapsed] = useState(false)
  const [showFilters, setShowFilters] = useState(true)
  const wide = useMedia('(min-width: 1280px)')
  const medium = useMedia('(min-width: 640px)')
  const columns = wide ? 4 : medium ? 2 : 1
  const attrFilters = serializeImageAttributeFilters(selection)
  const keywordsParam = keywords.length ? JSON.stringify(keywords) : undefined
  const contextKey = JSON.stringify([scope, imageType, attrFilters, keywordsParam])
  useEffect(() => {
    setPagination({ contextKey, page: 1 })
  }, [contextKey])
  const page = pagination.contextKey === contextKey ? pagination.page : 1
  const setPage = (page: number) => setPagination({ contextKey, page })
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['create-image-view', scope, imageType, attrFilters, keywordsParam, page],
    queryFn: async ({ signal }) => {
      const result = await API.getImageList(
        {
          page,
          per_page: BOOT_SOURCE_PAGE_SIZE,
          attr_filters: attrFilters,
          keywords: keywordsParam,
          owned: imageType === GhostType.CustomImage || undefined,
        },
        { signal },
      )
      if (result.code !== 0) throw new Error(result.msg || 'Failed to fetch image page')
      return {
        total: result.pagination.total,
        images: (result.images ?? []).map((item) => new Image(item)),
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  })
  const images = data?.images ?? []
  const total = data?.total ?? 0
  useEffect(() => {
    if (
      !isPending &&
      image &&
      (isError ||
        !images.some((item) => item.resID === image.resID && !isImageLaunchUnavailable(item)))
    ) {
      onMachineChange(undefined)
      onImageChange(undefined)
    }
  }, [images, isPending, isError, image, onImageChange, onMachineChange])
  const rows = Array.from({ length: Math.ceil(images.length / columns) }, (_, index) =>
    images.slice(index * columns, (index + 1) * columns),
  )
  const clearSelection = () => {
    onMachineChange(undefined)
    onImageChange(undefined)
    setPage(1)
    setCollapsed(false)
  }

  return (
    <section aria-busy={isPending} data-ai-component="image-machine-selector">
      <div className="mb-4 flex flex-wrap items-end gap-4">
        {toolbarStart}
        <div className="w-40">
          <div className="t-sb text-secondary mb-2">{t('create.imageView.imageType')}</div>
          <Dropdown
            value={imageType}
            options={[
              { value: GhostType.Image, label: t('create.typeLabelAllImages') },
              { value: GhostType.CustomImage, label: t('create.typeLabelCustomImage') },
            ]}
            onChange={(value) => {
              clearSelection()
              setImageType(Number(value))
            }}
          />
        </div>
        <div className={showFilters ? 'contents' : 'hidden'}>
          <ImageAttributeFilters
            value={selection}
            onChange={(value) => {
              clearSelection()
              setSelection(value)
            }}
          />
        </div>
        {toolbarEnd}
        <button
          type="button"
          className="ml-auto flex h-11 items-center rounded-lg border border-neutral-200 p-3 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
          aria-label={t('create.imageView.toggleFilters')}
          aria-expanded={showFilters}
          onClick={() => setShowFilters(!showFilters)}
        >
          <span
            aria-hidden="true"
            className="block size-5 shrink-0 bg-current"
            style={{
              mask: `url(${ConfigureIcon}) center / contain no-repeat`,
              WebkitMask: `url(${ConfigureIcon}) center / contain no-repeat`,
            }}
          />
        </button>
      </div>
      <p className="mb-4 t-xs text-tertiary">{t('create.imageView.keywordSearchHint')}</p>
      {isPending ? (
        <div className="flex justify-center p-16">
          <Loading />
        </div>
      ) : isError ? (
        <div role="alert" className="p-8 text-center">
          <p className="mb-4 t-sm">{t('create.imageView.loadFailed')}</p>
          <Button type="outline" onClick={() => void refetch()}>
            {t('create.imageView.retry')}
          </Button>
        </div>
      ) : !images.length ? (
        <EmptyView content={t('create.imageView.noImages')} />
      ) : (
        <>
          <div className="space-y-4" role="group" aria-label={t('create.bootSourceType.image')}>
            {rows.map((row) => (
              <Fragment key={row[0].resID}>
                <div
                  className="grid gap-4"
                  style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
                >
                  {row.map((item) => {
                    const unavailable = isImageLaunchUnavailable(item)
                    const selected = !unavailable && image?.resID === item.resID
                    return (
                      <Tooltip
                        key={item.resID}
                        title={unavailable ? t('create.imageView.unavailableStatus') : undefined}
                        trigger={['hover', 'focus']}
                      >
                        <button
                          aria-disabled={unavailable}
                          type="button"
                          aria-pressed={selected}
                          aria-expanded={selected && !collapsed}
                          aria-controls={selected ? 'image-machines' : undefined}
                          className={cn(
                            'relative flex h-[248px] flex-col gap-3 rounded-lg border border-neutral-200 p-5 text-left',
                            {
                              'border-secondary bg-highlight/10': selected,
                              'opacity-50 cursor-not-allowed': unavailable,
                            },
                          )}
                          data-ai-component="image-option"
                          data-ai-value={item.resID}
                          data-ai-state={
                            unavailable ? 'disabled' : selected ? 'selected' : 'available'
                          }
                          onClick={() => {
                            if (unavailable) return
                            if (selected) {
                              setCollapsed(!collapsed)
                              return
                            }
                            onMachineChange(undefined)
                            onImageChange(item)
                            setCollapsed(false)
                          }}
                        >
                          <img
                            src={IMAGE_BRAND_ICONS[getImageBrand(item.alias)]}
                            alt=""
                            className="h-10 w-10 shrink-0 rounded-lg bg-white object-contain p-1"
                            data-image-brand={getImageBrand(item.alias)}
                          />
                          <div className="min-h-[58px]">
                            <div className="t-sb text-primary line-clamp-2">{item.alias}</div>
                            <p className="mt-1.5 line-clamp-2 t-xs text-secondary">
                              {item.description}
                            </p>
                          </div>
                          {unavailable && (
                            <p className="line-clamp-2 t-xs text-secondary">
                              {t('create.imageView.unavailableStatus')}
                            </p>
                          )}
                          <div className="mt-auto flex w-full items-center justify-between gap-2 border-t border-neutral-200 pt-3 t-xs">
                            <span className="truncate text-secondary">{item.resID}</span>
                            {unavailable ? (
                              <span className="text-secondary">
                                {t('create.imageView.unavailable')}
                              </span>
                            ) : (
                              <span className="flex shrink-0 items-center gap-1 text-highlight">
                                {t('create.imageView.viewMachines')}
                                <span className={selected && !collapsed ? '' : 'rotate-180'}>
                                  <img src={MachineActionIcon} alt="" />
                                </span>
                              </span>
                            )}
                          </div>
                          {selected && !collapsed && (
                            <span
                              aria-hidden="true"
                              className="absolute -bottom-[7px] left-1/2 size-3 rotate-45 border-b border-r border-secondary bg-panel-white"
                            />
                          )}
                        </button>
                      </Tooltip>
                    )
                  })}
                </div>
                {image &&
                  row.some(
                    (item) => item.resID === image.resID && !isImageLaunchUnavailable(item),
                  ) && (
                    <div
                      id="image-machines"
                      className={cn('rounded-lg border border-secondary p-6', {
                        hidden: collapsed,
                      })}
                    >
                      <div className="mb-4 flex items-center justify-between gap-4">
                        <h3 className="t-sb">{t('create.imageView.machinesForImage')}</h3>
                        <span className="ml-auto truncate t-xs text-secondary">
                          {image.resID} · {image.alias}
                        </span>
                        <button
                          type="button"
                          className="t-sm text-secondary"
                          onClick={() => setCollapsed(true)}
                        >
                          {t('create.imageView.collapseMachines')}
                        </button>
                      </div>
                      <MachineSelector
                        key={`${scope}:${image.resID}`}
                        imageResId={image.resID}
                        type={type}
                        period={PaymentType.Hourly}
                        onTypeChange={onTypeChange}
                        onChange={onMachineChange}
                        selectedMachine={machine}
                        count={count}
                        checkedImage={image}
                        setCheckedImage={onImageChange}
                        setCheckedSnap={() => {}}
                        enforceBootSourceResourceLimit
                        supportsExtraResourcesForBootSource={supportsExtraResources}
                        billingMethod={billingMethod}
                        duration={duration}
                        workloadTypes={['all']}
                      />
                    </div>
                  )}
              </Fragment>
            ))}
          </div>
        </>
      )}
      {!isPending && !isError && total > 0 && (
        <BootSourcePagination
          current={page}
          selected={image ? 1 : 0}
          total={total}
          serverPaginated
          onChange={(value) => {
            onMachineChange(undefined)
            onImageChange(undefined)
            setPage(value)
          }}
        />
      )}
    </section>
  )
}
